import React, { useContext, useEffect, useState } from 'react';
import { BankAccount } from '../../../app/models/BankAccountType';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import DefaultModal from '../../../components/default/DefaultModal';
import SocietyBankAccountCreate from './SocietyBankAccountCreate';
import useBankAccountService from '../../../app/services/hooks/useBankAccount';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import { AppContext } from '../../../contexts/AppContext';

interface Props {
    societyId: number;
}

const SocietyBankAccountContainer = ({ societyId }: Props) => {
    const { getBankAccountBySociety, deleteBankAccount } = useBankAccountService();
    const { showLoading, hideLoading } = useContext(AppContext);
    
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [showingCreateBankAccount, setShowingCreateBankAccount] = useState(false);

    useEffect(() => {
        getBankAccounts(societyId);
    }, []);

    const getBankAccounts = (id: number) => {
        getBankAccountBySociety(id, {
            onSuccess: (response: ServiceResponse) => {
                setBankAccounts(response.data.bank_accounts);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    const destroy = (id: number) => {
        const _text = 'Está a punto de eliminar la Cuenta Bancaria #' + id;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando...');
                        deleteBankAccount(id, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                toast.success(response.message);
                                getBankAccounts(societyId);
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                toast.error(response.message);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const showCreate = () => {
        setShowingCreateBankAccount(true);
    };

    const hideCreate = () => {
        setShowingCreateBankAccount(false);
    };

    const onSaved = () => {
        getBankAccounts(societyId);
        hideCreate();
    };

    return (
        <DefaultCard>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Cuentas Corrientes</h5>
                <ButtonCreate callbackCreate={showCreate} title="Agregar Cuenta" />
            </div>

            {bankAccounts.length === 0 ? (
                <div className="alert alert-info">
                    No hay cuentas corrientes registradas para esta sociedad.
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Banco</th>
                                <th>Tipo de Cuenta</th>
                                <th>Número de Cuenta</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bankAccounts.map((account) => (
                                <tr key={account.id}>
                                    <td>{account.bank?.name}</td>
                                    <td>{account.account_type?.name}</td>
                                    <td>{account.account_number}</td>
                                    <td className="text-center">
                                        <ButtonTableAction
                                            callbackFunction={() => account.id && destroy(account.id)}
                                            classIcon="mdi mdi-delete"
                                            colorIcon="text-danger"
                                            errorMessage="No se puede eliminar esta cuenta."
                                            title="Eliminar"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DefaultModal
                title="Crear Cuenta Corriente"
                show={showingCreateBankAccount}
                handleClose={hideCreate}
                size="lg"
                showFooter={false}
            >
                <SocietyBankAccountCreate
                    societyId={societyId}
                    onSaved={onSaved}
                    onCancel={hideCreate}
                />
            </DefaultModal>
        </DefaultCard>
    );
};

export default SocietyBankAccountContainer;
